import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { physicalDeliveryChargesPkr } from '../utils/physicalDelivery.js';
import { isIssuePurchasable, issuePurchaseBlockedMessage } from '../utils/issuePurchaseWindow.js';

const CART_KEY = 'flare_store_cart_v1';

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((l) => {
      if (!l || !l.issueId || (l.delivery_type !== 'Digital' && l.delivery_type !== 'Physical')) return false;
      // Drop archive issues left in an old cart.
      return isIssuePurchasable({
        is_purchasable: l.snapshot?.is_purchasable,
        publication_date: l.snapshot?.publication_date,
        month_year: l.snapshot?.month_year,
      });
    });
  } catch (e) {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadFromStorage);

  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch (e) {}
  }, [items]);

  const addItem = useCallback((issue, { delivery_type = 'Digital', quantity = 1 } = {}) => {
    if (!issue?.id) return { ok: false, message: 'Issue not found.' };
    if (!isIssuePurchasable(issue)) {
      return { ok: false, message: issuePurchaseBlockedMessage() };
    }
    setItems(prev => {
      const matchIdx = prev.findIndex(i => i.issueId === issue.id && i.delivery_type === delivery_type);
      if (matchIdx >= 0) {
        const next = [...prev];
        const q = (prev[matchIdx].quantity || 0) + (quantity || 1);
        next[matchIdx] = { ...prev[matchIdx], quantity: Math.max(1, q) };
        return next;
      }
      return [...prev, {
        cartId: `${issue.id}-${delivery_type}-${Date.now()}`,
        issueId: issue.id,
        delivery_type,
        quantity: Math.max(1, quantity),
        snapshot: {
          id: issue.id,
          title: issue.title,
          slug: issue.slug,
          month_year: issue.month_year,
          publication_date: issue.publication_date,
          cover_image_url: issue.cover_image_url,
          pdf_file_url: issue.pdf_file_url,
          digital_price_pkr: Number(issue.digital_price_pkr) || 0,
          physical_price_pkr: Number(issue.physical_price_pkr) || 0,
          is_physical_available: !!issue.is_physical_available,
          is_purchasable: true,
        },
      }];
    });
    return { ok: true };
  }, []);

  const removeItem = useCallback((cartId) => {
    setItems(prev => prev.filter(i => i.cartId !== cartId));
  }, []);

  const updateQuantity = useCallback((cartId, nextQty) => {
    const q = Math.max(1, Number.isFinite(Number(nextQty)) ? Number(nextQty) : 1);
    setItems(prev => prev.map(i => i.cartId === cartId ? { ...i, quantity: q } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const unitPrice = (item) => {
    if (item.delivery_type === 'Digital') return Number(item.snapshot?.digital_price_pkr) || 0;
    return Number(item.snapshot?.physical_price_pkr) || 0;
  };

  const lineTotal = (item) => unitPrice(item) * (item.quantity || 0);

  const hasPhysical = items.some(i => i.delivery_type === 'Physical');
  const physicalCopyCount = items
    .filter((i) => i.delivery_type === 'Physical')
    .reduce((n, i) => n + Math.max(1, i.quantity || 1), 0);

  const subtotal = items.reduce((s, i) => s + lineTotal(i), 0);
  const deliveryCharges = physicalDeliveryChargesPkr(physicalCopyCount);
  const total = subtotal + deliveryCharges;
  const totalUniqueItems = items.reduce((n, i) => n + (i.quantity || 0), 0);

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    unitPrice,
    lineTotal,
    hasPhysical,
    subtotal,
    deliveryCharges,
    total,
    totalUniqueItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
